import { useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CrestPiece, CrestShapeData } from "@legacy-manager/engine";
import { useT } from "../../i18n";
import {
  CATEGORIAS,
  buscarFiguras,
  carregarCatalogo,
  catalogoEmMemoria,
  pecaDaFigura,
  type CategoriaDeFigura,
  type EstiloDeFigura,
  type Figura,
} from "./catalogo";

/**
 * A grade de onde a figura da peça sai.
 *
 * **Duas abas, e não uma lista misturada.** Traço cheio e traço de contorno não
 * combinam dentro do mesmo escudo, e separá-los na escolha evita a mistura antes
 * dela nascer. As abas também são a divisão dos arquivos: cada uma busca o seu,
 * e quem só usa figura cheia nunca baixa os contornos.
 *
 * **Nada aqui escreve marcação.** Cada resultado desenha as mesmas formas puras
 * do escudo, como elementos de verdade. É regra de segurança e não de estilo: o
 * mesmo caminho que desenha a miniatura desenha o escudo que chega pela rede no
 * online, e marcação crua ali seria injeção de script.
 */

/**
 * Quantas figuras entram por vez na grade.
 *
 * As 1240 de uma vez travam o navegador, e não é exagero de cautela: cada figura
 * cheia é um `path` de centenas de comandos, então a grade inteira passa de cem
 * mil nós de SVG. O navegador para de responder no meio da digitação, que é
 * justamente quando a grade se refaz.
 *
 * Sessenta é o que cobre a tela mais alta sem rolar, então quem digitou uma
 * busca boa nem percebe que existe limite. O resto entra ao rolar, de sessenta
 * em sessenta.
 */
const POR_VEZ = 60;

/** Lado da miniatura, em pixels. Cabe no alvo de toque sem encostar na borda. */
const MINIATURA = 28;

/**
 * Uma forma solta, desenhada na grade de origem dela.
 *
 * É a mesma conversão que o escudo faz. A cor não vem do dado: aqui ela é
 * `currentColor`, para a miniatura acompanhar o estado do botão e acender junto
 * com a borda quando o ponteiro passa por cima.
 */
function FormaDaFigura({ forma }: { forma: CrestShapeData }): ReactNode {
  if (forma.t === "p") return <path d={forma.d} />;
  if (forma.t === "c") return <circle cx={forma.cx} cy={forma.cy} r={forma.r} />;
  if (forma.t === "e") return <ellipse cx={forma.cx} cy={forma.cy} rx={forma.rx} ry={forma.ry} />;
  return (
    <rect x={forma.x} y={forma.y} width={forma.w} height={forma.h} rx={forma.rx} ry={forma.ry} />
  );
}

/**
 * A figura em tamanho de escolha.
 *
 * O `viewBox` é a grade de origem, que muda entre os dois catálogos, e por isso
 * vem de fora. Contorno ganha traço de espessura 2 na grade de 24, que é a
 * espessura com que ele foi desenhado: mexer nela aqui deixaria a miniatura
 * diferente da peça que a pessoa vai ver no escudo.
 */
function Miniatura({
  figura,
  grade,
  contorno,
}: {
  figura: Figura;
  grade: number;
  contorno: boolean;
}): ReactNode {
  return (
    <svg
      width={MINIATURA}
      height={MINIATURA}
      viewBox={`0 0 ${grade} ${grade}`}
      aria-hidden="true"
      fill={contorno ? "none" : "currentColor"}
      stroke={contorno ? "currentColor" : "none"}
      strokeWidth={contorno ? 2 : undefined}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      {figura.formas.map((forma, indice) => (
        <FormaDaFigura key={indice} forma={forma} />
      ))}
    </svg>
  );
}

export function SeletorDeFigura({
  estilo,
  aoTrocarDeEstilo,
  aoEscolher,
}: {
  estilo: EstiloDeFigura;
  aoTrocarDeEstilo: (estilo: EstiloDeFigura) => void;
  /** Recebe a peça montada, com desenho, grade e contorno já dentro. */
  aoEscolher: (peca: CrestPiece) => void;
}): ReactNode {
  const t = useT();

  const [texto, setTexto] = useState("");
  const [categoria, setCategoria] = useState<CategoriaDeFigura | null>(null);
  const [visiveis, setVisiveis] = useState(POR_VEZ);

  /*
   * O catálogo é lido do cache do módulo a cada desenho, e o estado local existe
   * só para redesenhar quando o arquivo chega. Guardar o catálogo em estado
   * criaria uma segunda cópia da verdade, que fica velha no instante em que a
   * aba troca e mostra as figuras da aba anterior por um quadro.
   */
  const [, avisarQueChegou] = useState(0);
  const catalogo = catalogoEmMemoria(estilo);

  useEffect(() => {
    if (catalogoEmMemoria(estilo) !== null) return;

    // Trocar de aba durante a espera não pode acordar a aba antiga: o aviso da
    // busca que ficou para trás é descartado pela limpeza.
    let valendo = true;
    const avisar = (): void => {
      if (valendo) avisarQueChegou((conta) => conta + 1);
    };
    carregarCatalogo(estilo).then(avisar, avisar);

    return () => {
      valendo = false;
    };
  }, [estilo]);

  /*
   * A busca anda atrás do que se digita.
   *
   * Filtrar 1240 nomes é barato, refazer sessenta figuras na tela não é. Com o
   * valor adiado, a letra aparece no campo na hora e a grade alcança depois, em
   * vez de a digitação engasgar a cada tecla.
   */
  const buscaAdiada = useDeferredValue(texto);

  const resultados = useMemo(
    () => (catalogo === null ? [] : buscarFiguras(catalogo, buscaAdiada, categoria)),
    [catalogo, buscaAdiada, categoria],
  );

  // Busca nova começa do topo: manter a contagem faria uma busca de três
  // resultados carregar seiscentas figuras invisíveis.
  useEffect(() => {
    setVisiveis(POR_VEZ);
  }, [estilo, buscaAdiada, categoria]);

  const rolagem = useRef<HTMLDivElement>(null);
  const fim = useRef<HTMLDivElement>(null);
  const temMais = resultados.length > visiveis;

  /*
   * O fim da lista é quem pede o próximo lote.
   *
   * Observar uma marca no fim custa nada e funciona com dedo, roda e teclado,
   * enquanto ouvir o evento de rolagem dispara dezenas de vezes por gesto. O
   * observador é refeito a cada lote de propósito: se a marca continuar visível
   * depois de crescer, a nova observação dispara de novo e a grade se completa
   * sozinha até encher a altura da caixa.
   */
  useEffect(() => {
    const alvo = fim.current;
    if (!temMais || alvo === null) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) {
          setVisiveis((atual) => atual + POR_VEZ);
        }
      },
      { root: rolagem.current, rootMargin: "200px" },
    );
    observador.observe(alvo);

    return () => observador.disconnect();
  }, [temMais, visiveis]);

  return (
    <div className="grid" style={{ gap: 8, minHeight: 0 }}>
      <span className="label">{t("criador.catalogo.titulo")}</span>

      <div className="row" role="tablist" style={{ gap: 0 }}>
        {(["cheios", "contornos"] as const).map((aba) => (
          <button
            key={aba}
            role="tab"
            aria-selected={estilo === aba}
            className={estilo === aba ? "" : "ghost"}
            onClick={() => aoTrocarDeEstilo(aba)}
            style={{ flex: 1, borderRadius: 0 }}
          >
            {t(`criador.catalogo.abas.${aba}`)}
          </button>
        ))}
      </div>

      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          value={texto}
          // O campo não tem rótulo em cima, então a dica também é o nome
          // acessível: sem isso o leitor de tela anuncia um campo sem nome.
          placeholder={t("criador.catalogo.busca")}
          aria-label={t("criador.catalogo.busca")}
          onChange={(evento) => setTexto(evento.target.value)}
          style={{ flex: 1, minWidth: 120 }}
        />
        <select
          value={categoria ?? ""}
          aria-label={t("criador.catalogo.titulo")}
          onChange={(evento) =>
            setCategoria(evento.target.value === "" ? null : (evento.target.value as CategoriaDeFigura))
          }
        >
          <option value="">{t("criador.catalogo.todas")}</option>
          {CATEGORIAS.map((chave) => (
            <option key={chave} value={chave}>
              {t(`criador.catalogo.categorias.${chave}`)}
            </option>
          ))}
        </select>
      </div>

      {/*
        Enquanto o arquivo não chega a caixa fica vazia, com `aria-busy`. A
        espera é de um pedido só e a grade aparece inteira; encher o lugar de
        esqueleto cinza faria a tela piscar duas vezes para o mesmo dado.
      */}
      <div
        ref={rolagem}
        className="scroll"
        aria-busy={catalogo === null}
        style={{ flex: 1, minHeight: 0, maxHeight: 320 }}
      >
        {catalogo === null ? null : resultados.length === 0 ? (
          <p className="muted" style={{ margin: 0, padding: 10 }}>
            {t("criador.catalogo.nadaEncontrado")}
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              // Alvo de 44 pixels, que é o mínimo de dedo. A grade decide
              // sozinha quantas colunas cabem, do celular em pé ao monitor.
              gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))",
              gap: 6,
            }}
          >
            {resultados.slice(0, visiveis).map((figura) => (
              <button
                key={figura.id}
                className="ghost"
                // O nome da figura é o único texto que identifica o botão, e ele
                // vem do catálogo em inglês: fica no rótulo acessível e na dica
                // do ponteiro, nunca desenhado na grade.
                title={figura.nome}
                aria-label={figura.nome}
                onClick={() => {
                  const peca = pecaDaFigura(catalogo, figura.id);
                  if (peca) aoEscolher(peca);
                }}
                style={{
                  padding: 0,
                  minHeight: 44,
                  height: 44,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Miniatura figura={figura} grade={catalogo.grade} contorno={catalogo.contorno} />
              </button>
            ))}

            {/* A marca que o observador enxerga, no fim do que já foi desenhado. */}
            {temMais && <div ref={fim} style={{ gridColumn: "1 / -1", height: 1 }} />}
          </div>
        )}
      </div>
    </div>
  );
}
